import { fmtMoney } from '../utils/currency';
import React, { useState, useEffect } from 'react';
import { useAuth } from '../context/AuthContext';
import {
  Package, Plus, X,
  Boxes, AlertTriangle, AlertCircle,
} from 'lucide-react';
import {
  useToast, useConfirm, usePrompt,
  SkeletonRow, EmptyState,
  PageHeader, StatusBadge, Button, Input,
} from '../components/ui';

const API = process.env.REACT_APP_API_URL || '/api';

const STAT_DEFS = [
  { key: 'total',  label: 'Total items',  color: 'primary', Icon: Boxes },
  { key: 'low',    label: 'Low stock',    color: 'warning', Icon: AlertTriangle },
  { key: 'out',    label: 'Out of stock', color: 'danger',  Icon: AlertCircle },
];

const TINT = {
  primary: 'bg-primary-50 text-primary-600',
  success: 'bg-success-50 text-success-600',
  warning: 'bg-warning-50 text-warning-600',
  danger:  'bg-danger-50 text-danger-600',
};

const Inventory = () => {
  const { user } = useAuth();
  const { toast } = useToast();
  const confirm = useConfirm();
  const prompt = usePrompt();
  const [items, setItems] = useState([]);
  const [menuUsage, setMenuUsage] = useState({}); // { invId: [{ name }, ...] }
  const [loading, setLoading] = useState(true);
  const [showModal, setShowModal] = useState(false);
  const [editingItem, setEditingItem] = useState(null);
  const [filterStatus, setFilterStatus] = useState('all');
  const [filterCategory, setFilterCategory] = useState('all');
  const [categories, setCategories] = useState([]);

  const [formData, setFormData] = useState({
    name: '',
    category: '',
    quantity: 0,
    unit: 'pcs',
    minThreshold: 10,
    price: '',
    supplier: ''
  });

  const fetchItems = async () => {
    try {
      const token = localStorage.getItem('token');
      const res = await fetch(`${API}/inventory?status=${filterStatus}&category=${filterCategory}`, {
        headers: { 'x-auth-token': token }
      });
      const data = await res.json();
      if (res.ok) setItems(data);
    } catch (err) {
      console.error('Error fetching inventory:', err);
    } finally {
      setLoading(false);
    }
  };

  const fetchCategories = async () => {
    try {
      const token = localStorage.getItem('token');
      const res = await fetch(`${API}/inventory/categories`, {
        headers: { 'x-auth-token': token }
      });
      const data = await res.json();
      if (res.ok) setCategories(data);
    } catch (err) {
      console.error('Error fetching categories:', err);
    }
  };

  // Fetch menu items once to compute which menu items reference each inventory item
  const fetchMenuUsage = async () => {
    try {
      const token = localStorage.getItem('token');
      const res = await fetch(`${API}/menu`, { headers: { 'x-auth-token': token } });
      const menu = await res.json();
      if (!res.ok || !Array.isArray(menu)) return;
      const map = {};
      menu.forEach(item => {
        (item.recipe || []).forEach(r => {
          const invId = r.inventoryItem?._id || r.inventoryItem;
          if (!invId) return;
          if (!map[invId]) map[invId] = [];
          map[invId].push({ _id: item._id, name: item.name });
        });
      });
      setMenuUsage(map);
    } catch (err) {
      // non-fatal — column just shows "—"
    }
  };

  useEffect(() => {
    fetchItems();
    fetchCategories();
  }, [filterStatus, filterCategory]);

  // Menu usage map doesn't depend on filters; load once
  useEffect(() => {
    fetchMenuUsage();
  }, []);

  const handleSubmit = async (e) => {
    e.preventDefault();
    try {
      const token = localStorage.getItem('token');
      const url = editingItem
        ? `${API}/inventory/${editingItem._id}`
        : `${API}/inventory`;
      const method = editingItem ? 'PUT' : 'POST';

      const res = await fetch(url, {
        method,
        headers: {
          'Content-Type': 'application/json',
          'x-auth-token': token
        },
        body: JSON.stringify(formData)
      });

      if (res.ok) {
        setShowModal(false);
        setEditingItem(null);
        resetForm();
        fetchItems();
        fetchCategories();
        toast.success(editingItem ? 'Item updated' : 'Item added');
      } else {
        const data = await res.json().catch(() => ({}));
        toast.error(data.msg || 'Could not save item');
      }
    } catch (err) {
      toast.error('Could not save item');
    }
  };

  const handleEdit = (item) => {
    setEditingItem(item);
    setFormData({
      name: item.name,
      category: item.category || '',
      quantity: item.quantity,
      unit: item.unit || 'pcs',
      minThreshold: item.minThreshold || 10,
      price: item.price || '',
      supplier: item.supplier || ''
    });
    setShowModal(true);
  };

  const handleRestock = async (item) => {
    const quantity = await prompt({
      title: `Restock "${item.name}"`,
      message: 'Enter the quantity to add to current stock.',
      placeholder: 'e.g. 10',
      defaultValue: '10',
      inputType: 'number',
      confirmText: 'Restock'
    });
    if (!quantity || isNaN(quantity)) return;

    try {
      const token = localStorage.getItem('token');
      await fetch(`${API}/inventory/${item._id}/restock`, {
        method: 'PUT',
        headers: {
          'Content-Type': 'application/json',
          'x-auth-token': token
        },
        body: JSON.stringify({ quantity: parseInt(quantity) })
      });
      toast.success(`Restocked ${item.name} by ${quantity}`);
      fetchItems();
    } catch (err) {
      toast.error('Failed to restock item');
    }
  };

  const handleDelete = async (id) => {
    const ok = await confirm({
      title: 'Delete inventory item?',
      message: 'This will permanently remove the item from your inventory.',
      variant: 'danger',
      confirmText: 'Delete'
    });
    if (!ok) return;

    try {
      const token = localStorage.getItem('token');
      await fetch(`${API}/inventory/${id}`, {
        method: 'DELETE',
        headers: { 'x-auth-token': token }
      });
      toast.success('Inventory item deleted');
      fetchItems();
    } catch (err) {
      toast.error('Failed to delete item');
    }
  };

  const resetForm = () => {
    setFormData({
      name: '',
      category: '',
      quantity: 0,
      unit: 'pcs',
      minThreshold: 10,
      price: '',
      supplier: ''
    });
  };

  const getStatusText = (item) => {
    if (item.quantity <= 0) return 'Out of Stock';
    if (item.quantity <= item.minThreshold) return 'Low Stock';
    return 'In Stock';
  };

  const statValue = (key) => {
    if (key === 'total') return items.length;
    if (key === 'low')   return items.filter(i => i.quantity > 0 && i.quantity <= i.minThreshold).length;
    if (key === 'out')   return items.filter(i => i.quantity <= 0).length;
    return 0;
  };

  const canManage = ['super_admin', 'admin', 'manager'].includes(user?.role);

  return (
    <>
      <PageHeader
        title="Inventory"
        subtitle="Track stock levels and trigger restocks when items run low."
        action={canManage && (
          <Button
            variant="primary"
            leftIcon={<Plus size={16} />}
            onClick={() => { resetForm(); setEditingItem(null); setShowModal(true); }}
          >
            Add Item
          </Button>
        )}
      />

      {/* Stats — Dashboard pattern */}
      <div className="grid grid-cols-1 sm:grid-cols-3 gap-4 mb-6">
        {STAT_DEFS.map(({ key, label, color, Icon }) => (
          <div key={key} className="bg-white rounded-lg border border-neutral-200 shadow-card p-5">
            <div className="flex items-center justify-between mb-3">
              <p className="text-xs font-semibold uppercase tracking-wider text-neutral-500">{label}</p>
              <div className={`w-9 h-9 rounded-lg flex items-center justify-center ${TINT[color]}`}>
                <Icon size={18} />
              </div>
            </div>
            <p className="text-3xl font-bold text-neutral-900 tabular-nums">{statValue(key)}</p>
          </div>
        ))}
      </div>

      {/* Filters */}
      <div className="bg-white rounded-lg border border-neutral-200 shadow-card p-4 mb-6 flex flex-wrap gap-3">
        <div className="w-48">
          <select
            value={filterStatus}
            onChange={(e) => setFilterStatus(e.target.value)}
            className="w-full border border-neutral-300 rounded-md bg-white px-3 py-2 text-sm focus:outline-none focus:ring-4 focus:ring-primary-200 focus:border-primary-500"
          >
            <option value="all">All status</option>
            <option value="low">Low stock</option>
            <option value="out">Out of stock</option>
          </select>
        </div>
        <div className="w-48">
          <select
            value={filterCategory}
            onChange={(e) => setFilterCategory(e.target.value)}
            className="w-full border border-neutral-300 rounded-md bg-white px-3 py-2 text-sm focus:outline-none focus:ring-4 focus:ring-primary-200 focus:border-primary-500"
          >
            <option value="all">All categories</option>
            {categories.map(cat => (
              <option key={cat} value={cat}>{cat}</option>
            ))}
          </select>
        </div>
      </div>

      {/* Items Table */}
      <div className="bg-white rounded-lg border border-neutral-200 shadow-card overflow-x-auto">
        <table className="app-table">
          <thead>
            <tr>
              <th>Name</th>
              <th>Category</th>
              <th>Quantity</th>
              <th>Unit</th>
              <th>Min Threshold</th>
              <th>Price</th>
              <th>Supplier</th>
              <th>Used in</th>
              <th>Status</th>
              {canManage && <th>Actions</th>}
            </tr>
          </thead>
          <tbody>
            {loading && Array.from({ length: 5 }).map((_, i) => (
              <SkeletonRow key={`s-${i}`} cols={canManage ? 10 : 9} />
            ))}
            {!loading && items.map(item => (
              <tr key={item._id}>
                <td className="font-medium">{item.name}</td>
                <td>{item.category || <span className="text-neutral-400">—</span>}</td>
                <td className="tabular-nums font-medium">{item.quantity}</td>
                <td className="text-neutral-600">{item.unit || 'pcs'}</td>
                <td className="tabular-nums text-neutral-600">{item.minThreshold}</td>
                <td className="tabular-nums">{fmtMoney(Number(item.price) || 0, true)}</td>
                <td className="text-neutral-600">{item.supplier || <span className="text-neutral-400">—</span>}</td>
                <td>
                  {(() => {
                    const usage = menuUsage[item._id] || [];
                    if (usage.length === 0) {
                      return <span className="text-neutral-400 text-xs">—</span>;
                    }
                    const names = usage.map(u => u.name);
                    const preview = names.slice(0, 2).join(', ');
                    const more = names.length > 2 ? ` +${names.length - 2}` : '';
                    return (
                      <span className="text-xs" title={names.join(', ')}>
                        <span className="font-semibold text-neutral-700 tabular-nums">{usage.length}</span>
                        <span className="text-neutral-500"> · {preview}{more}</span>
                      </span>
                    );
                  })()}
                </td>
                <td>
                  <StatusBadge
                    variant={item.quantity <= 0 ? 'danger' : item.quantity <= item.minThreshold ? 'warning' : 'success'}
                    dot
                  >
                    {getStatusText(item)}
                  </StatusBadge>
                </td>
                {canManage && (
                  <td>
                    <div className="flex flex-wrap gap-2">
                      <Button size="sm" variant="success" onClick={() => handleRestock(item)}>Restock</Button>
                      <Button size="sm" variant="secondary" onClick={() => handleEdit(item)}>Edit</Button>
                      <Button size="sm" variant="ghost" onClick={() => handleDelete(item._id)}>Delete</Button>
                    </div>
                  </td>
                )}
              </tr>
            ))}
            {!loading && items.length === 0 && (
              <tr>
                <td colSpan={canManage ? 10 : 9}>
                  <EmptyState
                    icon={Package}
                    title="No inventory items found"
                    message={filterStatus !== 'all' || filterCategory !== 'all'
                      ? 'Try changing the filters to see more items.'
                      : 'Add your first inventory item to start tracking stock.'}
                  />
                </td>
              </tr>
            )}
          </tbody>
        </table>
      </div>

      {/* Add/Edit Modal */}
      {showModal && (
        <div className="fixed inset-0 bg-black/40 flex items-center justify-center z-50 p-4">
          <div className="bg-white rounded-lg shadow-card-hover w-full max-w-md max-h-[90vh] overflow-y-auto">
            <div className="px-6 py-4 border-b border-neutral-200 flex items-center justify-between sticky top-0 bg-white">
              <h2 className="text-lg font-bold text-neutral-900">
                {editingItem ? 'Edit item' : 'Add new item'}
              </h2>
              <button
                type="button"
                onClick={() => { setShowModal(false); setEditingItem(null); }}
                className="text-neutral-400 hover:text-neutral-700 transition-colors"
              >
                <X size={18} />
              </button>
            </div>

            <form onSubmit={handleSubmit}>
              <div className="p-6 space-y-4">
                <Input
                  label="Name"
                  type="text"
                  value={formData.name}
                  onChange={(e) => setFormData({...formData, name: e.target.value})}
                  required
                />
                <div>
                  <Input
                    label="Category"
                    type="text"
                    value={formData.category}
                    onChange={(e) => setFormData({...formData, category: e.target.value})}
                    list="inventory-categories"
                    placeholder="Type or pick from list"
                  />
                  <datalist id="inventory-categories">
                    {categories.map(cat => (
                      <option key={cat} value={cat} />
                    ))}
                  </datalist>
                </div>

                <div className="grid grid-cols-2 gap-3">
                  <Input
                    label="Quantity"
                    type="number"
                    value={formData.quantity}
                    onChange={(e) => setFormData({...formData, quantity: parseInt(e.target.value) || 0})}
                  />
                  <div>
                    <label className="text-sm font-medium text-neutral-700 block mb-1">Unit</label>
                    <select
                      value={formData.unit}
                      onChange={(e) => setFormData({...formData, unit: e.target.value})}
                      className="w-full border border-neutral-300 rounded-md bg-white px-3 py-2 text-sm focus:outline-none focus:ring-4 focus:ring-primary-200 focus:border-primary-500"
                    >
                      <option value="pcs">Pieces</option>
                      <option value="kg">Kilograms</option>
                      <option value="g">Grams</option>
                      <option value="l">Liters</option>
                      <option value="ml">Milliliters</option>
                      <option value="pack">Pack</option>
                    </select>
                  </div>
                </div>

                <Input
                  label="Min threshold"
                  type="number"
                  value={formData.minThreshold}
                  onChange={(e) => setFormData({...formData, minThreshold: parseInt(e.target.value) || 0})}
                  helper="Trigger low-stock alerts at this level."
                />

                <Input
                  label="Price (per unit)"
                  type="number"
                  step="0.01"
                  value={formData.price}
                  onChange={(e) => setFormData({...formData, price: e.target.value})}
                />

                <Input
                  label="Supplier (optional)"
                  type="text"
                  value={formData.supplier}
                  onChange={(e) => setFormData({...formData, supplier: e.target.value})}
                />
              </div>

              <div className="px-6 py-4 border-t border-neutral-200 bg-neutral-50 rounded-b-lg flex gap-2 sticky bottom-0">
                <Button
                  type="button"
                  variant="secondary"
                  fullWidth
                  onClick={() => { setShowModal(false); setEditingItem(null); }}
                >
                  Cancel
                </Button>
                <Button type="submit" variant="primary" fullWidth>
                  {editingItem ? 'Update item' : 'Add item'}
                </Button>
              </div>
            </form>
          </div>
        </div>
      )}
    </>
  );
};

export default Inventory;
